import { useEffect, useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useAuth } from '@/app/providers/authContext';
// Submodule path, not the `@/components/layout` barrel: the bell lives in that barrel's top bar,
// and going through the index would close a cycle between the shell and this feature.
import { profilesForNavigationItem } from '@/components/layout/navigation';
import { api, isApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query';

import {
  LOCKED_REASON,
  type InactivityNotice,
  type InactivityStatus,
  type ReactivationRequestResult,
} from './contracts';

const INACTIVITY = '/v1/inactivity';

/**
 * Whether this person is one the inactivity rule speaks to: whoever buys for a customer — the same
 * profiles as the cart (`RouteGroup.INACTIVITY_NOTICES` mirrors `ORDERS`). Anyone else would only
 * ever get a 403 from the bell, and a bell that always fails to load is worse than none.
 */
export function useInactivityAudience(): boolean {
  const { profile } = useAuth();
  return profile !== null && profilesForNavigationItem('cart').includes(profile);
}

function useInactivityKey(): readonly unknown[] {
  const { tenantId } = useAuth();
  return useMemo(() => [...queryKeys.all(tenantId ?? 'sem-tenant'), 'inactivity'], [tenantId]);
}

/**
 * Whether the customer is locked out, and its blocked units. Answered by the API even while every
 * other tenant route refuses, which is why the shell asks it first.
 */
export function useInactivityStatusQuery(): UseQueryResult<InactivityStatus, Error> {
  const key = useInactivityKey();
  const audience = useInactivityAudience();
  return useQuery({
    queryKey: [...key, 'status'],
    queryFn: ({ signal }) => api.get<InactivityStatus>(`${INACTIVITY}/status`, { signal }),
    enabled: audience,
  });
}

export function useInactivityNoticesQuery(): UseQueryResult<InactivityNotice[], Error> {
  const key = useInactivityKey();
  const audience = useInactivityAudience();
  return useQuery({
    queryKey: [...key, 'notices'],
    queryFn: async ({ signal }) => {
      const list = await api.get<{ items: InactivityNotice[] }>(`${INACTIVITY}/notices`, {
        signal,
      });
      return [...list.items];
    },
    enabled: audience,
  });
}

/** Writes to the cached list first, so the × feels instant; the server is told right after. */
function useNoticesCache() {
  const queryClient = useQueryClient();
  const key = [...useInactivityKey(), 'notices'];
  return {
    update(change: (notices: InactivityNotice[]) => InactivityNotice[]) {
      const previous = queryClient.getQueryData<InactivityNotice[]>(key);
      if (previous !== undefined) {
        queryClient.setQueryData(key, change(previous));
      }
      return previous;
    },
    restore(previous: InactivityNotice[] | undefined) {
      if (previous !== undefined) {
        queryClient.setQueryData(key, previous);
      }
    },
    refresh() {
      return queryClient.invalidateQueries({ queryKey: key });
    },
  };
}

/** The floating warning's ×: closed for today, still in the bell. */
export function useDismissNoticeMutation(): UseMutationResult<
  void,
  Error,
  string,
  InactivityNotice[] | undefined
> {
  const cache = useNoticesCache();
  return useMutation({
    mutationFn: (noticeId: string) =>
      api.post<undefined>(`${INACTIVITY}/notices/${noticeId}/dismiss`, undefined),
    onMutate: (noticeId) =>
      cache.update((notices) =>
        notices.map((notice) =>
          notice.id === noticeId ? { ...notice, dismissed_today: true } : notice,
        ),
      ),
    onError: (_error, _id, previous) => {
      cache.restore(previous);
    },
  });
}

/** The bell's ×, or "Excluir todas" with `null`. */
export function useDeleteNoticesMutation(): UseMutationResult<
  void,
  Error,
  string | null,
  InactivityNotice[] | undefined
> {
  const cache = useNoticesCache();
  return useMutation({
    mutationFn: (noticeId: string | null) =>
      api.delete<undefined>(
        noticeId === null ? `${INACTIVITY}/notices` : `${INACTIVITY}/notices/${noticeId}`,
      ),
    onMutate: (noticeId) =>
      cache.update((notices) =>
        noticeId === null ? [] : notices.filter((notice) => notice.id !== noticeId),
      ),
    onError: (_error, _id, previous) => {
      cache.restore(previous);
    },
  });
}

export interface ReactivationRequestInput {
  readonly establishmentId: string;
  readonly message?: string | undefined;
}

/** "Pedir reativação". Asking twice answers the open request; the API says which (`created`). */
export function useRequestReactivationMutation(): UseMutationResult<
  ReactivationRequestResult,
  Error,
  ReactivationRequestInput
> {
  const queryClient = useQueryClient();
  const key = useInactivityKey();
  return useMutation({
    mutationFn: ({ establishmentId, message }: ReactivationRequestInput) =>
      api.post<ReactivationRequestResult>(`${INACTIVITY}/reactivation-requests`, {
        establishment_id: establishmentId,
        ...(message === undefined || message.trim() === '' ? {} : { message: message.trim() }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...key, 'status'] }),
  });
}

/**
 * The customer's status, when it is locked out — `undefined` otherwise.
 *
 * Asked once on entry, and again the moment any request answers 403 `customer_locked_inactivity`: the job
 * may block the last unit while someone is mid-session, and the next refused request is how the
 * shell finds out, rather than leaving them clicking through refusals.
 */
export function useLockedStatus(): InactivityStatus | undefined {
  const queryClient = useQueryClient();
  const key = useInactivityKey();
  const status = useInactivityStatusQuery();

  useEffect(() => {
    const refresh = (error: unknown) => {
      if (isApiError(error) && error.status === 403 && error.problem?.reason === LOCKED_REASON) {
        void queryClient.invalidateQueries({ queryKey: [...key, 'status'] });
      }
    };
    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        refresh(event.action.error);
      }
    });
    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        refresh(event.action.error);
      }
    });
    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient, key]);

  return status.data?.account_locked === true ? status.data : undefined;
}
